/**
 * PTEL Visor - Marker Icon Component
 * Componente React para iconos de marcadores accesibles
 *
 * V09 - Medidas: E02 (contraste), E04 (feedback), A04 (alt), M02 (iconos por tipo)
 * 
 * @see src/constants/markerConfig.ts - Configuración de colores e iconos
 */

import React, { memo, useMemo } from 'react';
import { InfrastructureType } from '../../types/infrastructure';
import { 
  getMarkerConfig, 
  getMarkerAltText,
} from '../../constants/markerConfig';

// =============================================================================
// TIPOS
// =============================================================================

/** Tamaños predefinidos para marcadores */
export type MarkerSize = 'sm' | 'md' | 'lg' | 'xl';

/** Estado visual del marcador */
export type MarkerVisualState = 'normal' | 'hover' | 'selected' | 'disabled';

/** Props del componente MarkerIcon */
export interface MarkerIconProps {
  /** Tipo de infraestructura PTEL */
  type: InfrastructureType;
  /** Nombre específico de la infraestructura (para alt text) */
  name?: string;
  /** Tamaño del marcador */
  size?: MarkerSize;
  /** Estado visual */
  state?: MarkerVisualState;
  /** Mostrar tooltip con nombre */
  showTooltip?: boolean;
  /** Callback cuando se hace clic */
  onClick?: () => void;
  /** Callback cuando recibe foco */
  onFocus?: () => void;
  /** ID para accesibilidad */
  id?: string;
  /** Clases CSS adicionales */
  className?: string;
  /** Mostrar solo el icono emoji (sin contenedor SVG) */
  emojiOnly?: boolean;
  /** Mostrar abreviatura en lugar de emoji */
  useAbbr?: boolean;
}

// =============================================================================
// CONSTANTES
// =============================================================================

/** Dimensiones por tamaño */
const SIZE_MAP: Record<MarkerSize, { width: number; height: number; fontSize: number }> = {
  sm: { width: 24, height: 32, fontSize: 12 },
  md: { width: 32, height: 42, fontSize: 16 },
  lg: { width: 40, height: 52, fontSize: 20 },
  xl: { width: 48, height: 62, fontSize: 24 },
};

/** Opacidad por estado */
const OPACITY_MAP: Record<MarkerVisualState, number> = {
  normal: 1,
  hover: 1,
  selected: 1,
  disabled: 0.5,
};

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Icono de marcador accesible para el mapa PTEL
 * 
 * Renderiza un SVG con forma de marcador (pin) que incluye:
 * - Color según tipo de infraestructura
 * - Emoji o abreviatura como contenido
 * - Alt text accesible para lectores de pantalla
 * - Estados visuales (normal, hover, selected, disabled)
 * 
 * @example
 * ```tsx
 * <MarkerIcon type={InfrastructureType.HEALTH} name="Centro de Salud San Antón" />
 * <MarkerIcon type={InfrastructureType.FIRE} size="lg" state="selected" />
 * ```
 */
export const MarkerIcon = memo<MarkerIconProps>(function MarkerIcon({
  type,
  name,
  size = 'md',
  state = 'normal',
  showTooltip = false,
  onClick,
  onFocus,
  id,
  className = '',
  emojiOnly = false,
  useAbbr = false,
}) {
  const config = useMemo(() => getMarkerConfig(type), [type]);
  const altText = useMemo(() => getMarkerAltText(type, name), [type, name]);
  const dimensions = SIZE_MAP[size];
  const opacity = OPACITY_MAP[state];

  // Modo emoji simple
  if (emojiOnly) {
    return (
      <span
        role="img"
        aria-label={altText}
        className={className}
        style={{ fontSize: dimensions.fontSize }}
      >
        {config.icon}
      </span>
    );
  }

  const content = useAbbr ? config.abbr : config.icon;
  const isInteractive = onClick !== undefined;
  const tabIndex = isInteractive ? 0 : -1;

  // Calcular escala para estados
  const scale = state === 'hover' ? 1.1 : state === 'selected' ? 1.15 : 1;

  return (
    <svg
      id={id}
      width={dimensions.width * scale}
      height={dimensions.height * scale}
      viewBox={`0 0 ${dimensions.width} ${dimensions.height}`}
      role="img"
      aria-label={altText}
      tabIndex={tabIndex}
      onClick={onClick}
      onFocus={onFocus}
      onKeyDown={(e) => {
        if (isInteractive && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onClick?.();
        }
      }}
      className={`marker-icon marker-icon--${config.group} ${className}`}
      style={{
        cursor: isInteractive ? 'pointer' : 'default',
        opacity,
        transition: 'transform 0.15s ease-out, opacity 0.15s ease-out',
        outline: 'none',
      }}
      data-type={type}
      data-state={state}
    >
      {/* Título para tooltip nativo y accesibilidad */}
      {showTooltip && <title>{name || config.label}</title>}

      {/* Sombra del marcador */}
      <ellipse
        cx={dimensions.width / 2}
        cy={dimensions.height - 4}
        rx={dimensions.width / 4}
        ry={3}
        fill="rgba(0,0,0,0.2)"
      />

      {/* Cuerpo del marcador (pin shape) */}
      <MarkerShape
        width={dimensions.width}
        height={dimensions.height}
        color={config.color}
        state={state}
      />

      {/* Contenido (emoji o abreviatura) */}
      <text
        x={dimensions.width / 2}
        y={dimensions.height / 2 - 4}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={config.contrastColor}
        fontSize={dimensions.fontSize}
        fontFamily="system-ui, -apple-system, sans-serif"
        style={{ pointerEvents: 'none' }}
      >
        {content}
      </text>

      {/* Indicador de estado seleccionado */}
      {state === 'selected' && (
        <circle
          cx={dimensions.width / 2}
          cy={dimensions.height / 2 - 4}
          r={dimensions.width / 2 + 4}
          fill="none"
          stroke="#3b82f6"
          strokeWidth={2}
          strokeDasharray="4 2"
        />
      )}

      {/* Anillo de foco visible (accesibilidad) */}
      <circle
        cx={dimensions.width / 2}
        cy={dimensions.height / 2 - 4}
        r={dimensions.width / 2 + 2}
        fill="none"
        stroke="transparent"
        strokeWidth={2}
        className="marker-icon__focus-ring"
      />

      <style>{`
        .marker-icon:focus-visible .marker-icon__focus-ring {
          stroke: #3b82f6;
        }
        .marker-icon:hover {
          filter: brightness(1.1);
        }
      `}</style>
    </svg>
  );
});

// =============================================================================
// COMPONENTES AUXILIARES
// =============================================================================

interface MarkerShapeProps {
  width: number;
  height: number;
  color: string;
  state: MarkerVisualState;
}

/**
 * Forma SVG del marcador (pin)
 */
const MarkerShape = memo<MarkerShapeProps>(function MarkerShape({
  width,
  height,
  color,
  state,
}) {
  const cx = width / 2;
  const cy = height / 2 - 4;
  const r = width / 2 - 2;
  
  // Path para forma de pin: círculo + punta inferior
  const d = `
    M ${cx} ${height - 8}
    L ${cx - r * 0.4} ${cy + r * 0.7}
    A ${r} ${r} 0 1 1 ${cx + r * 0.4} ${cy + r * 0.7}
    Z
  `;

  return (
    <>
      {/* Relleno principal */}
      <path
        d={d}
        fill={color}
        stroke={state === 'selected' ? '#1e3a8a' : 'rgba(0,0,0,0.2)'}
        strokeWidth={state === 'selected' ? 2 : 1}
      />
      {/* Brillo sutil en la parte superior */}
      <ellipse
        cx={cx}
        cy={cy - r * 0.3}
        rx={r * 0.5}
        ry={r * 0.25}
        fill="rgba(255,255,255,0.3)"
      />
    </>
  );
});

// =============================================================================
// COMPONENTES DE CONVENIENCIA
// =============================================================================

/**
 * Leyenda de marcadores por grupo
 */
export interface MarkerLegendProps {
  /** Tipos a mostrar (por defecto todos) */
  types?: InfrastructureType[];
  /** Tamaño de los iconos */
  size?: MarkerSize;
  /** Orientación */
  orientation?: 'horizontal' | 'vertical';
  /** Clases CSS adicionales */
  className?: string;
}

export const MarkerLegend = memo<MarkerLegendProps>(function MarkerLegend({
  types,
  size = 'sm',
  orientation = 'vertical',
  className = '',
}) {
  const typesToShow = types ?? Object.values(InfrastructureType);

  return (
    <div
      className={`marker-legend marker-legend--${orientation} ${className}`}
      role="list"
      aria-label="Leyenda de tipos de infraestructura"
      style={{
        display: 'flex',
        flexDirection: orientation === 'vertical' ? 'column' : 'row',
        gap: '8px',
        flexWrap: 'wrap',
      }}
    >
      {typesToShow.map((type) => {
        const config = getMarkerConfig(type);
        return (
          <div
            key={type}
            role="listitem"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <MarkerIcon type={type} size={size} emojiOnly />
            <span style={{ fontSize: '0.875rem' }}>{config.label}</span>
          </div>
        );
      })}
    </div>
  );
});

// =============================================================================
// EXPORTS
// =============================================================================

export default MarkerIcon;
